// Lazy load pdfjs-dist to avoid SSR canvas module error
let pdfjsLib: typeof import('pdfjs-dist') | null = null;
let workerInitialized = false;

const initPdfJs = async () => {
  if (!pdfjsLib) {
    pdfjsLib = await import('pdfjs-dist');
    
    if (!workerInitialized) {
      // Configure worker locally
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
      workerInitialized = true;
    }
  }
  return pdfjsLib;
};

export const loadPdfDocument = async (url: string) => {
  const pdfjs = await initPdfJs();
  const loadingTask = pdfjs.getDocument(url);
  const pdf = await loadingTask.promise;
  return pdf;
};

export const renderPdfPage = async (
  pdf: import('pdfjs-dist').PDFDocumentProxy, 
  pageNumber: number, 
  canvas: HTMLCanvasElement, 
  scale: number = 1
) => {
  await initPdfJs(); // Ensure pdfjs is loaded
  
  const page = await pdf.getPage(pageNumber);
  
  // Use devicePixelRatio for Hi-DPI displays (Retina, etc.)
  const devicePixelRatio = window.devicePixelRatio || 1;
  const scaledScale = scale * devicePixelRatio;
  const viewport = page.getViewport({ scale: scaledScale });
  
  const context = canvas.getContext('2d');
  if (!context) return null;

  // Set canvas size with device pixel ratio
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  
  // Scale canvas back to CSS size
  canvas.style.width = `${viewport.width / devicePixelRatio}px`;
  canvas.style.height = `${viewport.height / devicePixelRatio}px`;

  const renderContext = {
    canvasContext: context,
    viewport: viewport,
  };

  const renderTask = page.render(renderContext);
  
  // Return the task so caller can await it or cancel it
  return {
    renderTask,
    viewport: page.getViewport({ scale }) // Return CSS viewport for overlay sizing
  };
};

// Helper to load file object (from upload)
export const loadPDFFile = async (file: File) => {
  const arrayBuffer = await file.arrayBuffer();
  const pdfjs = await initPdfJs();
  
  // Load PDF document
  const loadingTask = pdfjs.getDocument({ data: arrayBuffer });
  const pdf = await loadingTask.promise;
  
  const pages = [];
  
  // Extract pages metadata (similar to logic in DocumentEditorContent)
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale: 1 });
    
    // Create thumbnail
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const thumbViewport = page.getViewport({ scale: 0.2 });
    canvas.width = thumbViewport.width;
    canvas.height = thumbViewport.height;
    
    if (context) {
        await page.render({
            canvasContext: context,
            viewport: thumbViewport
        }).promise;
    }
    
    pages.push({
      pageNumber: i,
      width: viewport.width,
      height: viewport.height,
      thumbnail: canvas.toDataURL(),
      imageUrl: '' 
    });
  }
  
  return {
    pdfData: arrayBuffer,
    pages
  };
};


